import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight } from 'lucide-react';
import type { OrganizationDataWithMembership } from '@/lib/types';

interface OrganizationInviteScreenProps {
  invite: OrganizationDataWithMembership;
  onAccept: (organizationId: number) => void;
  onReject: (organizationId: number) => void;
  isLoading?: boolean;
}

export function OrganizationInviteScreen({ 
  invite, 
  onAccept, 
  onReject, 
  isLoading = false 
}: OrganizationInviteScreenProps) {
  const handleAccept = () => {
    onAccept(invite.id);
  };

  const handleReject = () => {
    onReject(invite.id);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md rounded-lg border bg-white">
        <CardHeader className="text-center pb-4 pt-8">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
            <svg
              className="h-6 w-6 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <CardTitle className="text-xl font-semibold text-gray-900">
            Join Organization
          </CardTitle>
          <CardDescription className="text-gray-700">
            You've been invited to join
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {invite.name}
            </h3>
            <div className="flex items-center justify-center gap-2">
              <Badge variant="secondary" className="text-sm">
                {invite.membership.role}
              </Badge>
            </div>
          </div>

          <div className="text-center">
            <Button
              onClick={handleAccept}
              disabled={isLoading}
              size="lg"
              className="px-16 py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center space-x-2"
            >
              <span>{isLoading ? 'PROCESSING...' : 'ACCEPT INVITATION'}</span>
              {!isLoading && <ArrowRight className="h-4 w-4" />}
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            By accepting, you'll gain access to this organization's campaigns and data. You can also{' '}
            <button
              onClick={handleReject}
              disabled={isLoading}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              decline
            </button>
            {' '}this invitation.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}